import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, LogIn, Key, Wallet } from 'lucide-react';

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
        return LogIn;
      case 'api_key_created':
        return Key;
      case 'wallet_added':
        return Wallet;
      default:
        return FileText;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login':
        return 'bg-blue-500/10 text-blue-500';
      case 'api_key_created':
        return 'bg-emerald-500/10 text-emerald-500';
      case 'wallet_added':
        return 'bg-yellow-500/10 text-yellow-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <AdminLayout title="Audit Logs">
      <div className="space-y-6" data-testid="admin-audit-logs-page">
        <p className="text-muted-foreground">View security and activity logs</p>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : logs.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Audit Logs</h3>
              <p className="text-muted-foreground">Activity logs will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity ({logs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {logs.map((log) => {
                  const Icon = getActionIcon(log.action);
                  return (
                    <div 
                      key={log.id} 
                      className="flex items-center justify-between p-4 bg-card border border-gray-200 dark:border-white/10 rounded-lg shadow-sm"
                      data-testid={`log-row-${log.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActionColor(log.action).split(' ')[0]}`}>
                          <Icon className={`h-5 w-5 ${getActionColor(log.action).split(' ')[1]}`} />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{log.action.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            User ID: {log.user_id?.slice(0, 8)}...
                          </p>
                          {log.details?.email && (
                            <p className="text-xs text-muted-foreground">{log.details.email}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
